import { BookOpenText, FileText, NotebookText, Sparkles } from 'lucide-react'
import type { ClassroomMaterial } from '../api/backend'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'

interface ClassMaterialsDropdownProps {
  materials: ClassroomMaterial[]
  onSelect: (material: ClassroomMaterial) => void
}

function materialTypeLabel(type?: string | null) {
  if (!type) return 'Material'
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
}

function MaterialTypeIcon({ type }: { type?: string | null }) {
  if (type?.toUpperCase() === 'WORKSHEET') return <NotebookText className="size-4" />
  if (type?.toUpperCase() === 'REFERENCE') return <BookOpenText className="size-4" />
  return <FileText className="size-4" />
}

export function ClassMaterialsDropdown({ materials, onSelect }: ClassMaterialsDropdownProps) {
  if (materials.length === 0) {
    return (
      <div className="inline-flex items-center gap-2 rounded-base border-2 border-nb-border bg-bg px-2.5 py-1.5 text-sm font-heading text-foreground/40">
        <Sparkles className="size-3.5" />
        Ask from materials
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-2 rounded-base border-2 border-nb-border bg-bg px-2.5 py-1.5 text-sm font-heading text-foreground/70 transition-colors hover:bg-main hover:text-main-foreground"
          type="button"
        >
          <Sparkles className="size-3.5" />
          Ask from materials
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-60 w-64 overflow-y-auto bg-background text-foreground">
        {materials.map((material) => (
          <DropdownMenuItem
            key={material.id ?? material.title}
            className="bg-background text-foreground focus:bg-secondary-bg"
            onClick={() => onSelect(material)}
          >
            <div className="flex items-center gap-2">
              <div className="shrink-0 text-foreground/50">
                <MaterialTypeIcon type={material.kind} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm">{material.title}</p>
                {material.kind ? (
                  <p className="text-[10px] text-foreground/45">{materialTypeLabel(material.kind)}</p>
                ) : null}
              </div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
